import { TRPCClientError } from "@trpc/client";

import type { AppRouter } from "~/server/api/root";

function readNonEmptyMessage(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("message" in error)) {
    return null;
  }
  const message = Reflect.get(error, "message");
  if (typeof message !== "string") {
    return null;
  }
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const stripTrpcClientMessageNoise = (message: string): string => message.replace(/: WORKFLOW_ERROR$/i, "").trim();

/** User-facing message from a failed tRPC query or mutation. */
export function getTrpcErrorMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  if (error instanceof TRPCClientError) {
    const message = stripTrpcClientMessageNoise(error.message);
    if (message.length > 0) {
      return message;
    }
  }
  if (error instanceof Error) {
    const trimmed = stripTrpcClientMessageNoise(error.message);
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  const nestedMessage = readNonEmptyMessage(error);
  return nestedMessage ? stripTrpcClientMessageNoise(nestedMessage) : fallback;
}

export const isTrpcForbiddenError = (error: unknown): error is TRPCClientError<AppRouter> =>
  error instanceof TRPCClientError && error.data?.code === "FORBIDDEN";

/** Map a client-side FORBIDDEN mutation to module-specific permission copy. */
export const getRbacForbiddenMutationErrorMessage = (error: unknown, fallback: string, permissionMessage: string): string => {
  if (isTrpcForbiddenError(error)) {
    return permissionMessage;
  }
  return getTrpcErrorMessage(error, fallback);
};
